/**
 * Étiquettes d'un contact de la fiche : sa provenance (source avec lien, jamais
 * l'URL en clair) et son statut humain — vérifié, faux numéro, messagerie,
 * rebond, étiquette libre… ou « non vérifié ». Le menu ▾ change le statut.
 */
import { useEffect, useRef, useState } from "react";
import { BadgeCheck, ExternalLink, ShieldQuestion, ChevronDown, AlertTriangle, Info, X } from "lucide-react";

export const SHORT_SOURCE = {
  google_business: "Fiche Google", website: "Site", email: "Site", phone: "Site", sirene: "Registre",
  maps: "BAN", mairie_com: "Mairie.com", toiletteur_chien_chat: "Toiletteur-chien-chat", zouaf: "Zouaf",
  annuaire_118712: "118 712", ia: "IA",
};
export const shortSourceLabel = (source) => SHORT_SOURCE[source?.provider] || source?.provider_label || "Source";

/** Miroir de crm/contact_checks.py : le serveur fait foi et refuse un statut inconnu. */
export const CHECK_STATES = {
  telephone: [
    { value: "verified", label: "Vérifié", tone: "ok" },
    { value: "no_answer", label: "Ne répond pas", tone: "warn" },
    { value: "voicemail", label: "Messagerie seulement", tone: "warn" },
    { value: "switchboard", label: "Standard / accueil", tone: "info" },
    { value: "wrong", label: "Faux numéro", tone: "bad" },
    { value: "custom", label: "Autre…", tone: "info" },
    { value: "unverified", label: "Retirer l'étiquette", tone: "none" },
  ],
  email: [
    { value: "verified", label: "Vérifié", tone: "ok" },
    { value: "no_reply", label: "Sans réponse", tone: "warn" },
    { value: "generic", label: "Adresse générique", tone: "info" },
    { value: "bounced", label: "Rebond / invalide", tone: "bad" },
    { value: "custom", label: "Autre…", tone: "info" },
    { value: "unverified", label: "Retirer l'étiquette", tone: "none" },
  ],
  site_web: [
    { value: "verified", label: "Vérifié", tone: "ok" },
    { value: "offline", label: "Site hors ligne", tone: "warn" },
    { value: "wrong", label: "Mauvais site", tone: "bad" },
    { value: "custom", label: "Autre…", tone: "info" },
    { value: "unverified", label: "Retirer l'étiquette", tone: "none" },
  ],
};
export const familyOf = (field) => (field?.startsWith("telephone") ? "telephone" : field?.startsWith("email") ? "email" : "site_web");

const TONE = {
  ok: "bg-emerald-50 text-emerald-700", warn: "bg-amber-50 text-amber-700",
  bad: "bg-rose-50 text-rose-700", info: "bg-sky-50 text-sky-700", none: "bg-slate-100 text-slate-500",
};
const TONE_ICON = { ok: BadgeCheck, warn: AlertTriangle, bad: X, info: Info };
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");

export default function SourceTag({ value, source, check, onSet, field = "telephone" }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(null);      // null = pas de saisie libre en cours
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setCustom(null); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  if (!value) return null;

  const states = CHECK_STATES[familyOf(field)] || CHECK_STATES.telephone;
  const pick = (st) => {
    if (st.value === "custom") { setCustom(""); return; }
    setOpen(false); setCustom(null);
    onSet?.(st.value, null);
  };
  const submitCustom = () => {
    const label = (custom || "").trim();
    if (!label) return;
    setOpen(false); setCustom(null);
    onSet?.("custom", label);
  };

  const tags = [];
  if (check) {
    const Icon = TONE_ICON[check.tone] || BadgeCheck;
    tags.push(
      <span key="check" data-testid="tag-check" data-state={check.state || (check.verified ? "verified" : "")}
        title={`${check.label || "Vérifié"}${check.by_name ? ` — par ${check.by_name}` : ""}${check.at ? ` le ${fmtDay(check.at)}` : ""}`}
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ${TONE[check.tone] || TONE.ok}`}>
        <Icon size={11} /> {check.label || "Vérifié"}{check.at && ` · ${fmtDay(check.at)}`}
      </span>,
    );
  }
  if (source) {
    const label = shortSourceLabel(source);
    tags.push(source.source_url ? (
      <a key="src" href={source.source_url} target="_blank" rel="noreferrer" data-testid="tag-source"
        title={`${source.provider_label || label} — ${source.source_url}`}
        className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100">
        {label} <ExternalLink size={10} />
      </a>
    ) : (
      <span key="src" data-testid="tag-source" title={source.provider_label || label}
        className="inline-flex items-center rounded-md bg-indigo-50 px-1.5 py-0.5 text-[11px] font-medium text-indigo-700">{label}</span>
    ));
  }
  if (!check && !source) {
    tags.push(
      <span key="nv" data-testid="tag-unverified" title="Ni vérifié par un humain, ni sourcé par l'enrichissement"
        className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
        <ShieldQuestion size={11} /> Non vérifié
      </span>,
    );
  }
  return (
    <span className="relative inline-flex flex-wrap items-center gap-1" ref={ref}>
      {tags}
      {onSet && (
        <>
          <button type="button" data-testid="tag-menu" onClick={() => { setOpen((o) => !o); setCustom(null); }}
            title="Changer l'étiquette (vérifié, faux numéro, messagerie…)"
            className="inline-flex items-center gap-0.5 rounded-md border border-slate-200 px-1 py-0.5 text-[11px] text-slate-500 hover:border-indigo-300 hover:text-indigo-700">
            étiquette <ChevronDown size={10} />
          </button>
          {open && (
            <span className="absolute left-0 top-full z-20 mt-1 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid="tag-menu-list">
              {states.map((st) => (
                <button key={st.value} type="button" data-testid={`tag-state-${st.value}`} onClick={() => pick(st)}
                  className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-slate-50 ${check?.state === st.value ? "font-semibold" : ""} ${st.value === "unverified" ? "border-t border-slate-100 text-slate-500" : "text-slate-700"}`}>
                  <span className={`h-2 w-2 rounded-full ${TONE[st.tone]?.split(" ")[0] || "bg-slate-200"}`} /> {st.label}
                </button>
              ))}
              {custom !== null && (
                <span className="flex gap-1 border-t border-slate-100 px-2 py-1.5">
                  <input data-testid="tag-custom-input" autoFocus value={custom} onChange={(e) => setCustom(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitCustom()} placeholder="Étiquette libre"
                    className="min-w-0 flex-1 rounded border border-slate-300 px-1.5 py-1 text-[12px] outline-none focus:border-indigo-500" />
                  <button type="button" data-testid="tag-custom-save" onClick={submitCustom}
                    className="rounded bg-indigo-600 px-2 text-[12px] font-medium text-white">OK</button>
                </span>
              )}
            </span>
          )}
        </>
      )}
    </span>
  );
}

/** Texte court d'un lien : le domaine, pas l'URL entière. */
export function shortHost(url) {
  try { return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, ""); }
  catch { return url; }
}
